<script setup lang="ts">
import type PixInfoComponent from '@/components/withdraw/Info/Pix.vue';
import type SpeiInfoComponent from '@/components/withdraw/Info/Spei.vue';
import type AstroPayInfoComponent from '@/components/withdraw/Info/AstroPay.vue';

// Etapa 1 do saque (ENG-868): para onde o dinheiro vai.
//
// No Brasil isso é a chave Pix (o que o Figma desenha). Nos demais países o
// dado muda com a família do método — conta bancária mexicana, telefone —, e é
// por isso que esta etapa pergunta "que dado eu preciso pedir?" via
// withdrawMethodFamily() em vez de assumir Pix (SPEC §6.2).
//
// A lógica de cada formulário é a MESMA do fluxo antigo (decisão 4): os
// componentes WithdrawInfo* continuam resolvendo tudo e expondo o resultado
// pelos mesmos `defineExpose` que o CommomSection lia. Este componente veste a
// casca do Figma por cima (bloco :deep no final) e valida.

export type WithdrawAccountSelection = {
  selectedKeyType?: string;
  selectedKeyValue?: string;
  selectedBankAccountId?: number;
  selectedPhoneNumber?: string;
  selectedPhoneDdi?: string;
  selectedBankType?: string;
  selectedBankCode?: string;
  selectedBankNumber?: string;
  pixKeyError?: string;
};

const emit = defineEmits<{ submit: [WithdrawAccountSelection] }>();

const { close } = useWithdrawModal();

const { realWallet, currentWithdrawMethod } = storeToRefs(useWalletStore());
const { showMoneyFormat } = useWallet();
const { allWithdrawMethods, numberOfWithdrawMethods } = usePayments();
const { isLoading: isPaymentsLoading } = storeToRefs(usePaymentsStore());
const { userCountry, userCurrency, userLanguage } = useUserData();
const { walletHasFirstDeposit } = useWallet();

// Nenhum provedor de saque ativo pra moeda/país do jogador. A lista muda em
// runtime (/payments), então este estado é alcançável sem deploy — o fluxo
// antigo avisava (components/withdraw/index.vue) e aqui também.
const hasAnyWithdrawMethod = computed(() => numberOfWithdrawMethods.value >= 1);

// Quem ainda não depositou pode trocar país/moeda e passar a ter método
// disponível — mesma saída do fluxo antigo.
function goToChangeCountry() {
  close();

  navigateTo({
    path: localeLink('/user/account'),
    query: { highlight: 'infos' },
  });
}

const family = computed(() =>
  withdrawMethodFamily(currentWithdrawMethod.value),
);

// Os chips de gateway só aparecem aqui quando os métodos disponíveis pedem
// dados DIFERENTES entre si: nesse caso o jogador precisa escolher o gateway
// antes de preencher. No Brasil (tudo Pix) eles ficam só na Etapa 2, como no
// Figma.
const hasMixedFamilies = computed(() => {
  const families = new Set(
    allWithdrawMethods.value.map((m) => withdrawMethodFamily(m.slug)),
  );

  return families.size > 1;
});

const pixComponentRef = ref<InstanceType<typeof PixInfoComponent> | null>(null);
const speiComponentRef = ref<InstanceType<typeof SpeiInfoComponent> | null>(
  null,
);

const astropayComponentRef = ref<InstanceType<
  typeof AstroPayInfoComponent
> | null>(null);

const selectedKeyType = computed(
  () => pixComponentRef.value?.selectedKeyType ?? '',
);

const selectedKeyValue = computed(
  () => pixComponentRef.value?.selectedKeyValue ?? '',
);

const selectedBankAccountId = computed(
  () => pixComponentRef.value?.selectedBankAccountId,
);

const selectedPhoneNumber = computed(
  () =>
    pixComponentRef.value?.selectedPhoneNumber ??
    astropayComponentRef.value?.selectedPhoneNumber ??
    '',
);

const selectedPhoneDdi = computed(
  () =>
    pixComponentRef.value?.selectedPhoneDdi ??
    astropayComponentRef.value?.selectedPhoneDdi ??
    '',
);

const selectedBankType = computed(
  () => speiComponentRef.value?.selectedBankType ?? '',
);

const selectedBankCode = computed(
  () => speiComponentRef.value?.selectedBankCode ?? '',
);

const selectedBankNumber = computed(
  () => speiComponentRef.value?.selectedBankNumber ?? '',
);

const pixKeyError = computed(() => pixComponentRef.value?.pixKeyError ?? '');

// Saldo disponível pra saque: o `credit` da carteira real já vem em reais.
const availableAmount = computed(() =>
  showMoneyFormat({ value: realWallet.value.credit }),
);

// Validade por família. Pix e conta mexicana repetem as regras do fluxo antigo;
// crypto e worldline não coletam dado nenhum (são telas informativas), então
// não têm o que validar.
const isValid = computed<boolean>(() => {
  if (!hasAnyWithdrawMethod.value) return false;

  if (family.value === 'bank-mx') {
    return !!selectedBankType.value && !!selectedBankNumber.value;
  }

  if (family.value === 'phone') {
    return !!selectedPhoneNumber.value && !!selectedPhoneDdi.value;
  }

  if (family.value === 'pix') {
    return (
      !!selectedBankAccountId.value ||
      (!!selectedKeyType.value && !!selectedKeyValue.value)
    );
  }

  return true;
});

function onSubmit() {
  if (!isValid.value) return;

  emit('submit', {
    selectedKeyType: selectedKeyType.value,
    selectedKeyValue: selectedKeyValue.value,
    selectedBankAccountId: selectedBankAccountId.value,
    selectedPhoneNumber: selectedPhoneNumber.value,
    selectedPhoneDdi: selectedPhoneDdi.value,
    selectedBankType: selectedBankType.value,
    selectedBankCode: selectedBankCode.value,
    selectedBankNumber: selectedBankNumber.value,
    pixKeyError: pixKeyError.value,
  });
}
</script>

<template>
  <form
    class="wdm-account"
    @submit.prevent="onSubmit"
  >
    <!-- Gateways de famílias diferentes: a escolha precisa vir antes do
         formulário, senão o jogador preenche o dado errado. -->
    <WithdrawModalMethodTabs v-if="hasMixedFamilies" />

    <!-- Saldo disponível. No Figma ele fica ENTRE o select de tipo de chave e o
         campo da chave, mas os dois são internos ao PixKeys — intercalar exigiria
         furar aquele componente, então fica acima do bloco (ver SPEC §6.6). -->
    <p class="wdm-available">
      {{ $t('account.wallet.withdraw_modal.withdraw_amount_available') }}
      <strong>{{ availableAmount }}</strong>
    </p>

    <!-- /payments ainda em voo: sem este braço o `v-else` do switch de família
         faz o formulário de Pix ser o fallback, então um jogador MX via o
         seletor de chave Pix piscar antes da lista chegar. As etapas 1/2
         também perderam o overlay de carregamento que o fluxo antigo tinha. -->
    <BaseLoading
      v-if="isPaymentsLoading && !hasAnyWithdrawMethod"
      :full-screen="false"
      inline
      :show="true"
      size="small"
    />

    <template v-else-if="!hasAnyWithdrawMethod">
      <div
        class="alert alert-warning"
        role="alert"
      >
        {{
          $t('deposit.general.no_pay_method', {
            currency: userCurrency,
            country: userCountry,
          })
        }}
      </div>

      <button
        v-if="!walletHasFirstDeposit"
        class="alert alert-success"
        type="button"
        @click="goToChangeCountry"
      >
        {{ $t('deposit.general.can_change_country') }}
      </button>
    </template>

    <WithdrawInfoSpei
      v-else-if="family === 'bank-mx'"
      ref="speiComponentRef"
    />

    <WithdrawInfoAstroPay
      v-else-if="family === 'phone'"
      ref="astropayComponentRef"
    />

    <WithdrawInfoWorldline
      v-else-if="family === 'worldline'"
      :user-country="userCountry"
      :user-currency="userCurrency"
      :user-language="userLanguage"
    />

    <WithdrawInfoCrypto v-else-if="family === 'crypto'" />

    <WithdrawInfoPix
      v-else
      ref="pixComponentRef"
      :user-country="userCountry"
      :user-currency="userCurrency"
      :user-language="userLanguage"
    />

    <button
      class="wdm-submit"
      :disabled="!isValid"
      type="submit"
    >
      {{ $t('account.wallet.withdraw_modal.proceed') }}
    </button>
  </form>
</template>

<style scoped lang="scss">
$field-bg: #131a2c;
$field-text: #e2e3e5;
$accent: #38bdf8;
$warning: #f78a0c;
$cta: #ff3700;

.wdm-account {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.wdm-available {
  font-size: 12px;
  line-height: 16px;
  color: #dddee0;

  strong {
    color: $accent;
    font-weight: 700;
  }
}

.wdm-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  background: $cta;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;

  &[disabled]:not([disabled='false']) {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

// Casca do Figma por cima dos componentes WithdrawInfo* (e do
// BankAccountsPicker/PixKeys/Mex por baixo deles). Só aparência: se aquelas
// classes mudarem, esta etapa volta ao visual padrão dos campos do site —
// degrada, não quebra.
.wdm-account :deep(.componentBox) {
  gap: 12px;
}

.wdm-account :deep(label) {
  font-size: 12px;
  font-weight: 400;
  color: #dddee0;
}

// Campo de texto. Como no select, o BaseFormInput tem casco (.group, 46/50px +
// padding próprio) e <input> por dentro: vestir só o <input> deixava um campo de
// 36px dentro de uma caixa de 50px, com altura diferente do select ao lado.
.wdm-account :deep(.input-group) {
  width: 100%;
}

.wdm-account :deep(.input-group .group) {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  background: $field-bg;
}

.wdm-account :deep(input) {
  height: 36px;
  border-radius: 8px;
  background: $field-bg;
  color: $field-text;
  font-size: 13px;
}

// Select do tipo de chave Pix. O BaseFormSelectField tem dois níveis: o casco
// (.group, 46/50px + pt-2 + fundo próprio) e o <select> por dentro. Vestir só o
// <select> deixava o texto alto dentro de uma caixa clara de 50px, fora da
// paleta do modal — quem vira campo aqui é o casco inteiro.
.wdm-account :deep(.select-group) {
  width: 100%;
}

.wdm-account :deep(.select-group .group) {
  height: 36px;
  padding-top: 0;
  border-radius: 8px;
  background: $field-bg;
}

// O plugin de forms desenha a seta como background-image do <select>, e o
// `bg-transparent` do componente a apaga: sem isto o campo não se anuncia como
// select. Fica no casco porque <select> não aceita ::after.
.wdm-account :deep(.select-group .group)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 14px;
  width: 7px;
  height: 7px;
  margin-top: -6px;
  border-right: 2px solid rgba(255, 255, 255, 0.6);
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);
  transform: rotate(45deg);
  pointer-events: none;
}

.wdm-account :deep(.select-group .select) {
  height: 36px;
  padding: 0 32px 0 12px;
  color: $field-text;
  font-size: 13px;
  cursor: pointer;
}

// Lista nativa do dropdown (desktop): sem isto ela volta ao cinza do site.
.wdm-account :deep(.select-group .select option) {
  background: $field-bg;
  color: $field-text;
}

// Campo somente-leitura com a chave ("PIX: 123.123.123-12"): no site ele é um
// destaque em cima da cor primária; aqui é mais um campo do formulário, do mesmo
// tamanho do select ao lado.
// Casa pela classe estável `pix-key-info` do PixKeys: `.inputInfo` é CSS module
// e o nome sai hasheado no build, então um :deep(.inputInfo) nunca casaria.
// O `div` no seletor não é enfeite: sem ele a regra empata em especificidade com
// o `.componentBox .inputInfo` do PixKeys e quem ganha vira ordem de bundle.
.wdm-account :deep(div.pix-key-info) {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  background: $field-bg;
  color: $field-text;
  font-size: 13px;

  strong {
    color: $field-text;
  }
}

// Aviso de titularidade (account.user_data.pix_info), centralizado no Figma.
.wdm-account :deep(.thirdAlert) {
  margin: 0;
  padding: 0 4px;
  color: $warning;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
}
</style>
